// API client for Audiaro backend

// Use relative path in dev (proxied), full URL in production
const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.audiaro.com/v1';

export interface AdminCredential {
  id: string;
  key_name: string;
  display_name: string;
  provider: string;
  description: string | null;
  is_required: boolean;
  is_configured: boolean;
  verification_status: 'pending' | 'valid' | 'invalid';
  last_verified_at: string | null;
  masked_value?: string;
  updated_at: string;
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string | null;
  voice_id: string | null;
  provider: string | null;
  stitched_file_id: string | null;
  stitched_status: 'none' | 'stitching' | 'ready' | 'failed';
  created_at: string;
  updated_at: string;
  chapter_count?: number;
  completed_count?: number;
  total_characters?: number;
  // MP3 distribution metadata (returned on detail)
  author?: string | null;
  narrator?: string | null;
  genre?: string | null;
  publish_year?: string | null;
}

export interface ProjectChapter {
  id: string;
  idx: number;
  title: string;
  text_content: string;
  character_count: number;
  status: 'draft' | 'queued' | 'processing' | 'completed' | 'failed';
  job_id: string | null;
  audio_file_id: string | null;
  created_at: string;
  updated_at: string;
  public_url?: string | null;
  duration_seconds?: number | null;
}

export interface ProjectDetail extends ProjectSummary {
  share_token?: string | null;
  chapters: ProjectChapter[];
  stitched_file: {
    id: string;
    public_url: string;
    file_size: number;
    format: string;
    duration_seconds: number | null;
    created_at: string;
  } | null;
}

// Public (unauthenticated) audiobook player payload
export interface PublicAudiobookChapter {
  idx: number;
  title: string;
  public_url: string;
  duration_seconds: number | null;
}

export interface PublicAudiobook {
  name: string;
  description: string | null;
  language: string | null;
  created_at: string;
  chapters: PublicAudiobookChapter[];
  stitched_file: { public_url: string; duration_seconds: number | null } | null;
}

export interface PodcastVoiceRef {
  provider: string;
  id: string;
  name: string;
}

export interface PodcastShow {
  id: string;
  name: string;
  description: string | null;
  language: string | null;
  voice_a_provider: string | null;
  voice_a_id: string | null;
  voice_a_name: string | null;
  voice_b_provider: string | null;
  voice_b_id: string | null;
  voice_b_name: string | null;
  episode_count: number;
  completed_count: number;
  created_at: string;
  updated_at: string;
}

export interface PodcastEpisode {
  id: string;
  title: string;
  script: string;
  character_count: number;
  status: 'draft' | 'queued' | 'processing' | 'completed' | 'failed';
  audio_file_id: string | null;
  public_url: string | null;
  duration_seconds: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface PodcastShowDetail extends PodcastShow {
  episodes: PodcastEpisode[];
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  pagination?: {
    limit: number;
    offset: number;
    total?: number;
  };
  error?: {
    code: string;
    message: string;
    details?: Record<string, string>;
  };
}

interface AuthPayload {
  user: { id: string; email: string; first_name: string | null; last_name: string | null; role: string; tier_id: string };
  token: string;
  expires_at: string;
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json() as ApiResponse<T>;

      if (!response.ok) {
        return {
          success: false,
          error: data.error || {
            code: 'UNKNOWN_ERROR',
            message: `Request failed with status ${response.status}`,
          },
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  // Auth endpoints
  // Note: /auth/login and /auth/register return user/token at the TOP level of
  // the response, not wrapped in `data` like most other endpoints.
  async register(data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
  }) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<ApiResponse & Partial<AuthPayload>>;
  }

  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }) as Promise<ApiResponse & Partial<AuthPayload>>;
  }

  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  async getMe() {
    return this.request<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: string;
      tier: { id: string; name: string; slug: string };
      subscription_status: string | null;
      email_verified: boolean;
      created_at: string;
    }>('/auth/me');
  }

  async updateProfile(data: { first_name?: string; last_name?: string }) {
    return this.request<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: string;
      tier: { id: string; name: string; slug: string };
      subscription_status: string | null;
      email_verified: boolean;
      created_at: string;
    }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: { current_password: string; new_password: string }) {
    return this.request('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyEmail(token: string) {
    return this.request<never>('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }) as Promise<ApiResponse & { message?: string }>;
  }

  async resendVerificationEmail() {
    return this.request<never>('/auth/email/resend', {
      method: 'POST',
    }) as Promise<ApiResponse & { message?: string }>;
  }

  // API key endpoints
  async getApiKeys() {
    return this.request<Array<{
      id: string;
      name: string;
      key_prefix: string;
      last_used_at: string | null;
      created_at: string;
      is_active: boolean;
    }>>('/api-keys');
  }

  async createApiKey(name: string) {
    return this.request<{
      id: string;
      name: string;
      key: string; // Full key - returned only once at creation time
      key_prefix: string;
      created_at: string;
      is_active: boolean;
    }>('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteApiKey(keyId: string) {
    return this.request(`/api-keys/${keyId}`, { method: 'DELETE' });
  }

  // Audio endpoints
  async generateAudio(data: {
    text: string;
    voice_id: string;
    provider?: string;
    name?: string;
    settings?: {
      speed?: number;
      pitch?: number;
      format?: string;
      voice_name?: string;
    };
  }) {
    return this.request<{
      job_id: string;
      status: string;
      character_count: number;
      estimated_wait_seconds: number;
    }>('/audio/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getJob(jobId: string) {
    return this.request<{
      job_id: string;
      status: string;
      progress: number;
      character_count: number;
      provider: string;
      voice_id: string;
      queued_at: string;
      started_at: string | null;
      completed_at: string | null;
      audio_file?: {
        id: string;
        url: string;
        file_size: number;
        format: string;
        duration_seconds: number;
      };
      error?: {
        code: string;
        message: string;
      };
    }>(`/audio/jobs/${jobId}`);
  }

  async cancelJob(jobId: string) {
    return this.request(`/audio/jobs/${jobId}`, { method: 'DELETE' });
  }

  async getJobs(params?: { status?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    return this.request<Array<{
      job_id: string;
      character_count: number;
      provider: string;
      voice_id: string;
      voice_name: string | null;
      status: string;
      progress: number;
      source: string;
      queued_at: string;
      completed_at: string | null;
      audio_url: string | null;
      duration_seconds: number | null;
    }>>(`/audio/jobs?${query.toString()}`);
  }

  async getAudioFiles(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    return this.request<Array<{
      id: string;
      name: string;
      public_url: string;
      file_size: number;
      format: string;
      duration_seconds: number;
      text_preview: string;
      character_count: number;
      provider: string;
      voice_id: string;
      voice_name: string | null;
      created_at: string;
    }>>(`/audio/files?${query.toString()}`);
  }

  async deleteAudioFile(fileId: string) {
    return this.request(`/audio/files/${fileId}`, { method: 'DELETE' });
  }

  async renameAudioFile(fileId: string, name: string) {
    return this.request<{ id: string; name: string }>(`/audio/files/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  // Projects (audiobooks) endpoints
  async getProjects() {
    return this.request<ProjectSummary[]>('/projects');
  }

  async createProject(data: { name: string; description?: string; voice_id?: string; provider?: string; language?: string }) {
    return this.request<ProjectSummary>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(projectId: string) {
    return this.request<ProjectDetail>(`/projects/${projectId}`);
  }

  async updateProject(
    projectId: string,
    data: {
      name?: string;
      description?: string | null;
      voice_id?: string | null;
      provider?: string | null;
      author?: string | null;
      narrator?: string | null;
      genre?: string | null;
      publish_year?: string | null;
    }
  ) {
    return this.request<ProjectSummary>(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(projectId: string) {
    return this.request(`/projects/${projectId}`, { method: 'DELETE' });
  }

  async shareProject(projectId: string) {
    return this.request<{ share_token: string }>(`/projects/${projectId}/share`, { method: 'POST' });
  }

  async unshareProject(projectId: string) {
    return this.request<{ share_token: null }>(`/projects/${projectId}/share`, { method: 'DELETE' });
  }

  async getPublicAudiobook(token: string) {
    return this.request<PublicAudiobook>(`/public/audiobooks/${encodeURIComponent(token)}`);
  }

  async addChapter(projectId: string, data: { title: string; text: string }) {
    return this.request<ProjectChapter>(`/projects/${projectId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteChapterAudio(projectId: string, chapterId: string) {
    return this.request(`/projects/${projectId}/chapters/${chapterId}/audio`, { method: 'DELETE' });
  }

  async enhanceChapter(projectId: string, chapterId: string) {
    return this.request<{ chapter_id: string; character_count: number; text: string }>(
      `/projects/${projectId}/chapters/${chapterId}/enhance`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  }

  async generateChapter(projectId: string, chapterId: string) {
    return this.request<{ project_id: string; chapter_id: string; job_id: string }>(
      `/projects/${projectId}/chapters/${chapterId}/generate`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  }

  // Fetches the audio through the authenticated download endpoint so the file
  // can be saved with a clean name (the raw R2 URL has no CORS headers).
  async downloadAudioBlob(fileId: string): Promise<Blob | null> {
    const token = this.getToken();
    try {
      const res = await fetch(`${API_BASE}/audio/files/${fileId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return null;
      return await res.blob();
    } catch {
      return null;
    }
  }

  async updateChapter(projectId: string, chapterId: string, data: { title?: string; text?: string }) {
    return this.request<ProjectChapter>(`/projects/${projectId}/chapters/${chapterId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteChapter(projectId: string, chapterId: string) {
    return this.request(`/projects/${projectId}/chapters/${chapterId}`, { method: 'DELETE' });
  }

  async reorderChapters(projectId: string, chapterIds: string[]) {
    return this.request(`/projects/${projectId}/chapters/reorder`, {
      method: 'POST',
      body: JSON.stringify({ chapter_ids: chapterIds }),
    });
  }

  async generateProject(projectId: string, data?: { voice_id?: string; provider?: string }) {
    return this.request<{
      project_id: string;
      chapters_queued: number;
      total_characters: number;
      jobs: Array<{ chapter_id: string; job_id: string }>;
    }>(`/projects/${projectId}/generate`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async stitchProject(projectId: string) {
    return this.request<{
      project_id: string;
      stitched_status: string;
      chapters: number;
    }>(`/projects/${projectId}/stitch`, { method: 'POST' });
  }

  // Podcasts endpoints — a podcast (show) holds config, episodes hold scripts
  async getPodcasts() {
    return this.request<PodcastShow[]>('/podcasts');
  }

  async createPodcast(data: {
    name: string;
    description?: string;
    language?: string;
    voice_a?: PodcastVoiceRef;
    voice_b?: PodcastVoiceRef;
  }) {
    return this.request<PodcastShow>('/podcasts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPodcast(showId: string) {
    return this.request<PodcastShowDetail>(`/podcasts/${showId}`);
  }

  async updatePodcast(
    showId: string,
    data: {
      name?: string;
      description?: string;
      language?: string;
      voice_a?: PodcastVoiceRef;
      voice_b?: PodcastVoiceRef;
    }
  ) {
    return this.request<PodcastShow>(`/podcasts/${showId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePodcast(showId: string) {
    return this.request(`/podcasts/${showId}`, { method: 'DELETE' });
  }

  async addPodcastEpisode(showId: string, data: { title: string; script?: string }) {
    return this.request<PodcastEpisode>(`/podcasts/${showId}/episodes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePodcastEpisode(showId: string, episodeId: string, data: { title?: string; script?: string }) {
    return this.request<PodcastEpisode>(`/podcasts/${showId}/episodes/${episodeId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePodcastEpisode(showId: string, episodeId: string) {
    return this.request(`/podcasts/${showId}/episodes/${episodeId}`, { method: 'DELETE' });
  }

  async enhancePodcastEpisode(showId: string, episodeId: string) {
    return this.request<{ script: string; character_count: number }>(
      `/podcasts/${showId}/episodes/${episodeId}/enhance`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  }

  async generatePodcastEpisode(showId: string, episodeId: string) {
    return this.request<{ job_id: string }>(`/podcasts/${showId}/episodes/${episodeId}/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async deletePodcastEpisodeAudio(showId: string, episodeId: string) {
    return this.request(`/podcasts/${showId}/episodes/${episodeId}/audio`, { method: 'DELETE' });
  }

  // Voices endpoints
  async getVoices(params?: { provider?: string; language?: string; gender?: string }) {
    const query = new URLSearchParams();
    if (params?.provider) query.set('provider', params.provider);
    if (params?.language) query.set('language', params.language);
    if (params?.gender) query.set('gender', params.gender);

    return this.request<Array<{
      id: string;
      name: string;
      provider: string;
      language: string;
      language_code: string;
      gender: string;
      age?: string;
      styles?: string[];
      preview_url?: string;
    }>>(`/voices?${query.toString()}`);
  }

  // Custom voices (voice cloning)
  async getCustomVoices() {
    return this.request<Array<{
      id: string;
      voice_id: string;
      provider: string;
      name: string;
      language: string | null;
      status: string;
      created_at: string;
    }>>('/voices/custom');
  }

  async createCustomVoice(data: {
    name: string;
    sample: File;
    consent: boolean;
    language?: string;
    transcript?: string;
  }) {
    const form = new FormData();
    form.append('name', data.name);
    form.append('consent', data.consent ? 'true' : 'false');
    form.append('sample', data.sample, data.sample.name);
    if (data.language) form.append('language', data.language);
    if (data.transcript) form.append('transcript', data.transcript);

    // Multipart: let the browser set the Content-Type boundary itself
    const token = this.getToken();
    try {
      const response = await fetch(`${API_BASE}/voices/custom`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      const result = await response.json() as ApiResponse<{
        id: string;
        voice_id: string;
        provider: string;
        name: string;
        status: string;
      }>;
      if (!response.ok) {
        return {
          success: false as const,
          error: result.error || { code: 'UNKNOWN_ERROR', message: `Request failed with status ${response.status}` },
        };
      }
      return result;
    } catch (error) {
      return {
        success: false as const,
        error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' },
      };
    }
  }

  async deleteCustomVoice(id: string) {
    return this.request(`/voices/custom/${id}`, { method: 'DELETE' });
  }

  async getVoicePreview(voiceId: string, provider: string) {
    return this.request<{
      voice_id: string;
      provider: string;
      preview_url: string;
      cached: boolean;
    }>('/voices/preview', {
      method: 'POST',
      body: JSON.stringify({ voice_id: voiceId, provider }),
    });
  }

  // Billing endpoints
  async getSubscription() {
    return this.request<{
      tier: {
        id: string;
        name: string;
        slug: string;
        monthly_characters: number;
        price_monthly: number;
        price_yearly: number;
      };
      status: string;
      current_period_end: string | null;
      has_subscription: boolean;
    }>('/billing/subscription');
  }

  async getTiers() {
    return this.request<Array<{
      id: string;
      name: string;
      slug: string;
      description: string | null;
      limits: {
        monthly_characters: number;
        max_audio_length_seconds: number;
        max_text_length: number;
        concurrent_jobs: number;
      };
      providers: string[];
      output_formats: string[];
      features: Record<string, boolean>;
      pricing: {
        monthly: number;
        yearly: number;
        yearly_savings: number;
      };
    }>>('/billing/tiers');
  }

  async createCheckout(tierId: string, billingPeriod: 'monthly' | 'yearly') {
    return this.request<{
      checkout_url: string;
      session_id: string;
    }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier_id: tierId, billing_period: billingPeriod }),
    });
  }

  async createPortalSession() {
    return this.request<{
      portal_url: string;
    }>('/billing/portal', { method: 'POST' });
  }

  async getUsage() {
    return this.request<{
      tier_name: string;
      period: {
        start: string;
        end: string;
      };
      characters: {
        used: number;
        limit: number;
        remaining: number;
        percentage_used: number;
        by_source: {
          web: number;
          api: number;
          webhook: number;
        };
      };
      audio_files_generated: number;
      daily: Array<{
        date: string;
        characters_used: number;
        audio_files_generated: number;
      }>;
    }>('/billing/usage');
  }

  // Admin endpoints (require role === 'admin')
  async adminGetStats() {
    return this.request<{
      users: {
        total: number;
        active_subscriptions: number;
        by_tier: Array<{ name: string; count: number }>;
      };
      usage: {
        today: { characters: number; audio_files: number };
        this_month: { characters: number; audio_files: number };
      };
    }>('/admin/stats');
  }

  async adminGetUsers(params?: { limit?: number; offset?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.search) query.set('search', params.search);

    return this.request<Array<{
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: string;
      tier_id: string;
      tier_name: string;
      subscription_status: string | null;
      created_at: string;
    }>>(`/admin/users?${query.toString()}`);
  }

  async adminUpdateUser(userId: string, data: { role?: string; tier_id?: string }) {
    return this.request<{ message: string }>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async adminResetUserPassword(userId: string, newPassword: string) {
    return this.request<{ message: string }>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
  }

  async adminGetCredentials() {
    return this.request<{
      credentials: AdminCredential[];
      by_provider: Record<string, AdminCredential[]>;
    }>('/admin/credentials');
  }

  async adminSetCredential(keyName: string, value: string) {
    return this.request<{ message: string }>(`/admin/credentials/${keyName}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async adminClearCredential(keyName: string) {
    return this.request<{ message: string }>(`/admin/credentials/${keyName}`, {
      method: 'DELETE',
    });
  }

  async adminVerifyProvider(provider: string) {
    return this.request<{ message: string }>(`/admin/credentials/${provider}/verify`, {
      method: 'POST',
    });
  }

  async adminGetTiers() {
    return this.request<Array<{
      id: string;
      name: string;
      slug: string;
      monthly_characters: number;
      price_monthly: number;
      stripe_price_id_monthly: string | null;
      stripe_price_id_yearly: string | null;
      is_active: boolean;
    }>>('/admin/tiers');
  }

  async adminUpdateTier(tierId: string, data: Record<string, unknown>) {
    return this.request<{ message: string }>(`/admin/tiers/${tierId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async adminGetActivity(params?: { limit?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.request<Array<{
      id: string;
      admin_email: string;
      action: string;
      resource_type: string;
      resource_id: string | null;
      created_at: string;
    }>>(`/admin/activity?${query.toString()}`);
  }
}

export const api = new ApiClient();
